import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { generateDiffStrings } from 'utils/func/diffString';
import DiffColumn from '../DiffColumn';

export default function RecommendedExpertCompareTable({ data1, data2, diffObj }) {
  // #region Data
  const { t } = useTranslation();
  const recommendedExperts1 = useMemo(
    () => (data1 ?? [])?.map((item, index) => ({ ...item, id: index + 1 })),
    [data1]
  );
  const recommendedExperts2 = useMemo(
    () => (data2 ?? [])?.map((item, index) => ({ ...item, id: index + 1 })),
    [data2]
  );
  // #endregion Data

  // #region Render
  const formatIndex = (_, { rowIndex }) => rowIndex + 1;

  const formatFullname = (rowIndex, side = 'left') =>
    generateDiffStrings(
      recommendedExperts1?.[rowIndex]?.fullname,
      recommendedExperts2?.[rowIndex]?.fullname
    )?.[side];

  const formatResearchLine = (rowIndex, side = 'left') =>
    generateDiffStrings(
      recommendedExperts1?.[rowIndex]?.researchLine,
      recommendedExperts2?.[rowIndex]?.researchLine
    )?.[side];

  const formatWorkplace = (rowIndex, side = 'left') =>
    generateDiffStrings(
      recommendedExperts1?.[rowIndex]?.workplace,
      recommendedExperts2?.[rowIndex]?.workplace
    )?.[side];

  const formatPhoneAndEmail = (rowIndex, side = 'left') =>
    generateDiffStrings(
      `${recommendedExperts1?.[rowIndex]?.phone ?? ''}, ${
        recommendedExperts1?.[rowIndex]?.email ?? ''
      }`,
      `${recommendedExperts2?.[rowIndex]?.phone ?? ''}, ${
        recommendedExperts2?.[rowIndex]?.email ?? ''
      }`
    )?.[side];
  // #endregion Render

  return (
    <tr>
      <td>
        <div className="my-2">
          <DataTable
            value={recommendedExperts1}
            className="p-datatable-gridlines"
            showGridlines
            dataKey="id"
            scrollable
            responsiveLayout="scroll"
          >
            <Column
              header={t('formLayout.numericalOrder')}
              body={formatIndex}
              style={{ textAlign: 'center' }}
            />
            <Column
              header={t('projectProposal.general.recommendedExpert.fullname')}
              body={(_, { rowIndex }) => formatFullname(rowIndex, 'left')}
              style={{ minWidth: '4rem' }}
            />
            <Column
              header={t('projectProposal.general.recommendedExpert.researchLine')}
              body={(_, { rowIndex }) => formatResearchLine(rowIndex, 'left')}
              style={{ minWidth: '4rem' }}
            />
            <Column
              header={t('projectProposal.general.recommendedExpert.workplace')}
              body={(_, { rowIndex }) => formatWorkplace(rowIndex, 'left')}
              style={{ minWidth: '4rem' }}
            />
            <Column
              header={`${t('projectProposal.general.recommendedExpert.phone')}, ${t(
                'projectProposal.general.recommendedExpert.email'
              )}`}
              body={(_, { rowIndex }) => formatPhoneAndEmail(rowIndex, 'left')}
              style={{ minWidth: '4rem' }}
            />
          </DataTable>
        </div>
      </td>
      <td>
        <div className="my-2">
          <DataTable
            value={recommendedExperts2}
            className="p-datatable-gridlines"
            showGridlines
            dataKey="id"
            scrollable
            responsiveLayout="scroll"
          >
            <Column
              header={t('formLayout.numericalOrder')}
              body={formatIndex}
              style={{ textAlign: 'center' }}
            />
            <Column
              header={t('projectProposal.general.recommendedExpert.fullname')}
              body={(_, { rowIndex }) => formatFullname(rowIndex, 'right')}
              style={{ minWidth: '4rem' }}
            />
            <Column
              header={t('projectProposal.general.recommendedExpert.researchLine')}
              body={(_, { rowIndex }) => formatResearchLine(rowIndex, 'right')}
              style={{ minWidth: '4rem' }}
            />
            <Column
              header={t('projectProposal.general.recommendedExpert.workplace')}
              body={(_, { rowIndex }) => formatWorkplace(rowIndex, 'right')}
              style={{ minWidth: '4rem' }}
            />
            <Column
              header={`${t('projectProposal.general.recommendedExpert.phone')}, ${t(
                'projectProposal.general.recommendedExpert.email'
              )}`}
              body={(_, { rowIndex }) => formatPhoneAndEmail(rowIndex, 'right')}
              style={{ minWidth: '4rem' }}
            />
          </DataTable>
        </div>
      </td>
      <DiffColumn
        isDifferent={Object.values(diffObj ?? {})?.some(
          (item) =>
            item === true ||
            item?.fullname ||
            item?.researchLine ||
            item?.workplace ||
            item?.phone ||
            item?.email
        )}
      />
    </tr>
  );
}

RecommendedExpertCompareTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data1: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data2: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  diffObj: PropTypes.array.isRequired,
};
